// src/components/reservation/PrintCheckoutReceipt.js
import React, { useState } from 'react';
import { 
    Box, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { 
    Print as PrintIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { formatNumberWithComma, formatCreatedAtDate, getCategoriesNameById } from '../../utils/helpers';
import { companyInfo } from '../../utils/dataInfo';
import { printLogoIcon } from '../../assets';

const PrintCheckoutReceipt = ({ checkoutData, onPrintComplete, rooms = [] }) => {
    const [printError, setPrintError] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    const generateReceiptHTML = () => {
        const {
            booking,
            charges,
            payment,
            customerInfo
        } = checkoutData;

        // Format date and time
        const checkoutDate = new Date();
        const formattedDate = formatCreatedAtDate(checkoutDate);
        const formattedTime = checkoutDate.toLocaleTimeString();

        // Get room name
        const roomName = getCategoriesNameById(booking?.room_type, rooms) || booking?.room_number || 'N/A';

        // Get logo as base64 or data URL
        const logoUrl = printLogoIcon || '';

        return `
        <!DOCTYPE html>
        <html>
            <head>
                <title>Checkout Receipt - ${booking?.booking_no}</title>
                <style>
                    @page { 
                        size: A4;
                        margin: 15mm;
                    }
                    body { 
                        font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
                        max-width: 210mm;
                        margin: 0 auto;
                        padding: 10px;
                        font-size: 12px;
                        background: white;
                        color: #333;
                    }
                    .receipt-container {
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    }
                    .header { 
                        text-align: center;
                        padding: 20px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                    }
                    .logo-container {
                        text-align: center;
                        margin-bottom: 15px;
                    }
                    .logo {
                        max-width: 100%;
                        width: auto;
                        height: auto;
                        max-height: 80px;
                        background: white;
                        padding: 8px;
                        border-radius: 8px;
                        object-fit: contain;
                    }
                    .company-name { 
                        font-size: 24px; 
                        font-weight: bold; 
                        margin-bottom: 5px;
                        letter-spacing: 1px;
                    }
                    .company-info { 
                        font-size: 11px; 
                        margin: 3px 0;
                        opacity: 0.9;
                    }
                    .title { 
                        text-align: center; 
                        font-size: 18px; 
                        font-weight: bold; 
                        margin: 20px 0;
                        background: #4a5568;
                        color: white;
                        padding: 10px;
                        letter-spacing: 2px;
                    }
                    .content {
                        padding: 20px;
                    }
                    .info-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 8px 0; 
                        font-size: 12px;
                        padding: 4px 0;
                    }
                    .info-grid { 
                        display: grid; 
                        grid-template-columns: 1fr 1fr; 
                        gap: 12px; 
                        margin: 15px 0; 
                    }
                    .info-card {
                        background: #f7fafc;
                        padding: 12px;
                        border-radius: 8px;
                        border-left: 3px solid #667eea;
                    }
                    .info-label { 
                        color: #718096; 
                        font-size: 11px; 
                        text-transform: uppercase;
                        font-weight: 600;
                        margin-bottom: 5px;
                    }
                    .info-value { 
                        font-weight: bold;
                        font-size: 14px;
                        color: #2d3748;
                    }
                    .section-title {
                        font-weight: bold;
                        margin: 20px 0 10px 0;
                        padding: 8px;
                        background: #edf2f7;
                        text-align: center;
                        font-size: 14px;
                        border-radius: 5px;
                        color: #2d3748;
                    }
                    .items-table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        margin: 15px 0; 
                    }
                    .items-table th { 
                        text-align: left; 
                        border-bottom: 2px solid #e2e8f0;
                        padding: 10px 5px;
                        font-size: 12px;
                        font-weight: 600;
                        color: #4a5568;
                    }
                    .items-table td { 
                        padding: 8px 5px; 
                        border-bottom: 1px solid #e2e8f0;
                        font-size: 12px;
                    }
                    .items-table td:last-child, .items-table th:last-child { 
                        text-align: right; 
                    }
                    .text-right { 
                        text-align: right; 
                    }
                    .summary-section {
                        margin-top: 20px;
                        background: #f7fafc;
                        padding: 15px;
                        border-radius: 8px;
                    }
                    .total-row { 
                        font-weight: bold; 
                        margin-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 14px;
                        padding: 8px 0;
                    }
                    .grand-total {
                        font-weight: bold;
                        font-size: 18px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        padding: 12px 15px;
                        margin-top: 15px;
                        display: flex;
                        justify-content: space-between;
                        border-radius: 8px;
                    }
                    .payment-info {
                        margin-top: 20px;
                        padding: 15px;
                        background-color: #f0fff4;
                        border-radius: 8px;
                        border-left: 4px solid #48bb78;
                        font-size: 12px;
                    }
                    .status-badge {
                        text-align: center;
                        padding: 10px;
                        margin: 15px 0;
                        border-radius: 8px;
                        font-weight: bold;
                        font-size: 16px;
                    }
                    .status-paid {
                        background: #c6f6d5;
                        color: #22543d;
                    }
                    .status-due {
                        background: #feebc8;
                        color: #7b341e;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 30px; 
                        padding: 15px;
                        font-size: 10px;
                        border-top: 1px solid #e2e8f0;
                        color: #718096;
                        background: #f7fafc;
                    }
                    .thank-you { 
                        text-align: center; 
                        margin: 20px 0; 
                        font-size: 16px;
                        font-weight: bold;
                        color: #667eea;
                    }
                    .divider { 
                        border-top: 1px dashed #cbd5e0; 
                        margin: 15px 0; 
                    }
                    .notes { 
                        margin-top: 15px; 
                        padding: 12px; 
                        background: #fefcbf;
                        border-radius: 8px;
                        font-size: 11px;
                        border-left: 3px solid #ecc94b;
                    }
                    .small-text { 
                        font-size: 9px; 
                    }
                    .text-center { 
                        text-align: center; 
                    }
                    .print-footer {
                        text-align: center;
                        margin-top: 20px;
                        font-size: 9px;
                        color: #a0aec0;
                    }
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .no-print {
                            display: none;
                        }
                        .receipt-container {
                            box-shadow: none;
                            border: none;
                        }
                        .status-paid, .status-due {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .logo {
                            max-width: 100%;
                            max-height: 80px;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="receipt-container">
                    <div class="header">
                        ${logoUrl ? `
                        <div class="logo-container">
                            <img src="${logoUrl}" alt="Company Logo" class="logo" />
                        </div>
                        ` : ''}
                        <div class="company-name">${companyInfo.name}</div>
                        <div class="company-info">${companyInfo.address}</div>
                        <div class="company-info">Tel: ${companyInfo.phone} | Email: ${companyInfo.email || ''}</div>
                        <div class="company-info">VAT/TIN: ${companyInfo.tin || 'N/A'}</div>
                    </div>
                    
                    <div class="title">CHECKOUT RECEIPT</div>
                    
                    <div class="content">
                        <div class="info-grid">
                            <div class="info-card">
                                <div class="info-label">Booking Reference</div>
                                <div class="info-value">${booking?.booking_no || 'N/A'}</div>
                            </div>
                            <div class="info-card">
                                <div class="info-label">Checkout Date & Time</div>
                                <div class="info-value">${formattedDate} | ${formattedTime}</div>
                            </div>
                        </div>
                        
                        <div class="info-grid">
                            <div class="info-card">
                                <div class="info-label">Guest Name</div>
                                <div class="info-value">${customerInfo?.customerName || 'N/A'}</div>
                            </div>
                            <div class="info-card">
                                <div class="info-label">Customer Type</div>
                                <div class="info-value">${customerInfo?.customerType || 'N/A'}</div>
                            </div>
                        </div>
                        
                        <div class="info-grid">
                            <div class="info-card">
                                <div class="info-label">Room Number</div>
                                <div class="info-value">${roomName}</div>
                            </div>
                            <div class="info-card">
                                <div class="info-label">Number of Guests</div>
                                <div class="info-value">${customerInfo?.totalGuests || 0}</div>
                            </div>
                        </div>
                        
                        <div class="info-grid">
                            <div class="info-card">
                                <div class="info-label">Check-in Date</div>
                                <div class="info-value">${formatCreatedAtDate(booking?.check_in) || 'N/A'}</div>
                            </div>
                            <div class="info-card">
                                <div class="info-label">Check-out Date</div>
                                <div class="info-value">${formatCreatedAtDate(booking?.check_out) || 'N/A'}</div>
                            </div>
                        </div>
                        
                        <div class="info-grid">
                            <div class="info-card">
                                <div class="info-label">Nights Stayed</div>
                                <div class="info-value">${customerInfo?.nightsStayed || 0}</div>
                            </div>
                            <div class="info-card">
                                <div class="info-label">Processed By</div>
                                <div class="info-value">${payment?.processedBy || 'System'}</div>
                            </div>
                        </div>
                        
                        <div class="section-title">CHARGES BREAKDOWN</div>
                        
                        <table class="items-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th class="text-right">Amount (${companyInfo.currency || 'TZS'})</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Room Charges (${customerInfo?.nightsStayed || 0} nights)</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.roomCharges || 0)}</td>
                                </tr>
                                ${charges?.restaurantOrders > 0 ? `
                                <tr>
                                    <td>Restaurant Orders</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.restaurantOrders)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.barOrders > 0 ? `
                                <tr>
                                    <td>Bar Orders</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.barOrders)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.clubOrders > 0 ? `
                                <tr>
                                    <td>Club Orders</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.clubOrders)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.laundryCharges > 0 ? `
                                <tr>
                                    <td>Laundry Services</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.laundryCharges)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.spaCharges > 0 ? `
                                <tr>
                                    <td>Spa & Wellness</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.spaCharges)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.lateCheckoutCharges > 0 ? `
                                <tr>
                                    <td>Late Check-out Fee</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.lateCheckoutCharges)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.damageCharges > 0 ? `
                                <tr>
                                    <td>Damage Charges</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.damageCharges)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.otherCharges > 0 ? `
                                <tr>
                                    <td>Other Charges</td>
                                    <td class="text-right">${formatNumberWithComma(charges?.otherCharges)}</td>
                                </tr>
                                ` : ''}
                                ${charges?.discount > 0 ? `
                                <tr>
                                    <td>Discount</td>
                                    <td class="text-right" style="color: #48bb78;">-${formatNumberWithComma(charges?.discount)}</td>
                                </tr>
                                ` : ''}
                            </tbody>
                        </table>
                        
                        <div class="summary-section">
                            <div class="total-row">
                                <span>Subtotal:</span>
                                <span>${formatNumberWithComma(charges?.subtotal || 0)}</span>
                            </div>
                            
                            <div class="total-row">
                                <span>Tax (${charges?.taxRate || 0}%):</span>
                                <span>${formatNumberWithComma(charges?.taxAmount || 0)}</span>
                            </div>
                            
                            <div class="total-row">
                                <span>Service Charge:</span>
                                <span>${formatNumberWithComma(charges?.serviceCharge || 0)}</span>
                            </div>
                            
                            <div class="divider"></div>
                            
                            <div class="total-row">
                                <span>Total Charges:</span>
                                <span>${formatNumberWithComma(charges?.totalCharges || 0)}</span>
                            </div>
                            
                            <div class="total-row">
                                <span>Advance Payment / Deposit:</span>
                                <span>- ${formatNumberWithComma(payment?.advancePaid || 0)}</span>
                            </div>
                        </div>
                        
                        <div class="grand-total">
                            <span>BALANCE DUE:</span>
                            <span>${formatNumberWithComma(payment?.balanceDue || 0)}</span>
                        </div>
                        
                        <div class="payment-info">
                            <div class="info-row">
                                <span class="info-label">Payment Method:</span>
                                <span class="info-value" style="text-transform: uppercase; font-weight: bold;">${payment?.paymentMethod || 'N/A'}</span>
                            </div>
                            ${payment?.transactionId ? `
                            <div class="info-row">
                                <span class="info-label">Transaction ID:</span>
                                <span class="info-value">${payment.transactionId}</span>
                            </div>
                            ` : ''}
                            ${payment?.amountPaid > 0 ? `
                            <div class="info-row">
                                <span class="info-label">Amount Paid Today:</span>
                                <span class="info-value">${formatNumberWithComma(payment.amountPaid)}</span>
                            </div>
                            ` : ''}
                            ${payment?.paymentNotes ? `
                            <div class="info-row">
                                <span class="info-label">Payment Notes:</span>
                                <span class="info-value">${payment.paymentNotes}</span>
                            </div>
                            ` : ''}
                        </div>
                        
                        ${payment?.balanceDue === 0 ? `
                        <div class="status-badge status-paid">
                            ✓ FULLY PAID - THANK YOU ✓
                        </div>
                        ` : payment?.balanceDue > 0 ? `
                        <div class="status-badge status-due">
                            ⚠ AMOUNT DUE: ${formatNumberWithComma(payment.balanceDue)} ⚠
                        </div>
                        ` : ''}
                        
                        ${charges?.comments ? `
                            <div class="notes">
                                <strong>📝 Checkout Comments:</strong><br/>
                                ${charges.comments}
                            </div>
                        ` : ''}
                        
                        <div class="thank-you">
                            THANK YOU FOR STAYING WITH US!
                        </div>
                        
                        <div class="footer">
                            <div>${companyInfo.receiptFooter || 'We appreciate your business and hope to serve you again soon!'}</div>
                            <div style="margin-top: 8px;">
                                <strong>Contact us:</strong> ${companyInfo.phone} | ${companyInfo.email || ''}
                            </div>
                            <div style="margin-top: 8px;">
                                ${companyInfo.website ? `🌐 ${companyInfo.website}` : ''}
                            </div>
                        </div>
                        
                        <div class="print-footer">
                            This is a computer-generated receipt. No signature required.<br/>
                            Generated on: ${new Date().toLocaleString()}
                        </div>
                    </div>
                </div>
            </body>
        </html>
        `;
    };

    const handlePrint = () => {
        setIsPrinting(true);
        setPrintError(false);

        try {
            const printContent = generateReceiptHTML();
            
            const printWindow = window.open('', '_blank', 'width=900,height=700,toolbar=yes,scrollbars=yes,resizable=yes');
            
            if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
                setPrintError(true);
                setIsPrinting(false);
                return;
            }

            printWindow.document.write(printContent);
            printWindow.document.close();
            
            printWindow.focus();
            
            setTimeout(() => {
                try {
                    printWindow.print();
                    
                    printWindow.onafterprint = () => {
                        printWindow.close();
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    };
                    
                    setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close();
                        }
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    }, 2000);
                    
                } catch (printErr) {
                    console.error('Print error:', printErr);
                    setPrintError(true);
                    setIsPrinting(false);
                }
            }, 500);
            
        } catch (error) {
            console.error('Print error:', error);
            setPrintError(true);
            setIsPrinting(false);
        }
    };

    const handleManualPrint = () => {
        handlePrint();
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            handlePrint();
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    if (printError) {
        return (
            <Dialog open={printError} onClose={() => setPrintError(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <span>Print Error</span>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Pop-up blocker prevented the receipt from printing automatically.
                    </Alert>
                    <Typography variant="body2" paragraph>
                        Please click the button below to print manually, or check your browser settings to allow pop-ups for this site.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                            onClick={handleManualPrint}
                            size="large"
                        >
                            Print Receipt Manually
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setPrintError(false);
                            if (onPrintComplete) onPrintComplete();
                        }}
                        color="error"
                        startIcon={<CloseIcon />}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (isPrinting) {
        return (
            <Dialog open={isPrinting}>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography>Preparing receipt for printing...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Box sx={{ display: 'none' }}>
            {/* This component doesn't render anything visible when not printing */}
        </Box>
    );
};

export default PrintCheckoutReceipt;